/**
 * <CheckInDrawer> — canonical per AR-7. Launched from Guest Workspace.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  OperationalDrawer, BottomActionBar, FloorRoomPicker, OperationalButton,
} from "@/components/operational";
import { LogIn, Printer } from "lucide-react";
import { useCheckinGate } from "@/hooks/useCheckinGate";
import { EarlyCheckinPrompt } from "@/components/pms/EarlyCheckinPrompt";
import { foApi , formatApiError } from "../foApi";
import { requestRegCard } from "@/components/documents/RegCardOptions";
import { notifyDocument } from "@/components/documents/documentToast";

export default function CheckInDrawer({ open, onOpenChange, stay, onSuccess }) {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [checkedIn, setCheckedIn] = useState(null); // EARLY-CHECKIN · stay returned by /fo/checkin awaiting the prompt

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    foApi.rooms({ hotel_id: stay?.hotel_id }).then(r => {
      const ready = (r.items || []).filter(x => ["available", "vacant_clean", "ready", "clean"].includes(x.snapshot_status));
      setRooms(ready.length > 0 ? ready : (r.items || []));
    }).finally(() => setLoading(false));
    setSelected(null);
    setNote("");
  }, [open, stay?.hotel_id]);

  const gate = useCheckinGate();
  const gated = gate.isBlocked(stay?.expected_arrival);
  const submit = async () => {
    if (gated) { toast.error(gate.reason(stay.expected_arrival)); return; }
    if (!selected) { toast.error("Select a room"); return; }
    setSaving(true);
    try {
      const updated = await foApi.checkIn({ stay_id: stay.id, room_id: selected.id, note });
      notifyDocument("Registration card ready", { description: `${stay.guest_name} · Room ${selected.number}`, url: foApi.registrationCardUrl(stay.id),
        filename: `RegistrationCard-${stay.code}.pdf`, testId: "fo-checkin-regcard-toast", regCard: true });
      setCheckedIn({ stay_id: stay.id, folio_id: updated?.folio_id || stay.folio_id, stay_code: stay.code, room_number: selected.number, guest_name: stay.guest_name });
    } catch (e) {
      const body = e?.response?.data || {};
      const d = body.code ? body : body.detail;
      if (e?.response?.status === 409 && d?.code === "ALREADY_CHECKED_IN") {
        toast.warning("Guest is already checked in", { description: d.message });
        onOpenChange(false);
        if (d.stay_id) navigate(`/pms/stays/${d.stay_id}`);
      } else {
        toast.error(d?.code === "ROOM_OCCUPIED" ? d.message : formatApiError(e, "Check-in failed"));
      }
    } finally { setSaving(false); }
  };

  return (<>
    <EarlyCheckinPrompt open={!!checkedIn} stays={checkedIn ? [checkedIn] : []} onClose={() => { setCheckedIn(null); onSuccess?.(); }} />
    <OperationalDrawer open={open} onOpenChange={onOpenChange}
      title="Check-In · Select Room" subtitle={stay?.code}
      size="lg" testid="checkin-drawer"
      footer={
        <BottomActionBar
          primary={{ label: saving ? "Checking in..." : gated ? gate.reason(stay.expected_arrival) : "Confirm Check-In",
                       icon: LogIn, tone: "primary", onClick: submit,
                       loading: saving, disabled: !selected || gated, testid: "checkin-confirm" }}
          secondaryActions={[
            { label: "Print Reg Card", icon: Printer, tone: "secondary",
              onClick: () => stay?.id && requestRegCard({ url: foApi.registrationCardUrl(stay.id), filename: `RegistrationCard-${stay.code}.pdf` }),
              testid: "checkin-print-regcard" },
            { label: "Cancel", tone: "secondary", onClick: () => onOpenChange(false), testid: "checkin-cancel" }
          ]}
          helper={selected ? `Selected: Room ${selected.number}` : "Pick a ready room"}
        />
      }>
      <div className="p-4 space-y-3">
        <FloorRoomPicker rooms={rooms} loading={loading}
                          selectedRoomId={selected?.id}
                          onSelect={setSelected}
                          allowedStatuses={["available", "vacant_clean", "ready", "clean"]}
                          testid="checkin-room-picker"/>
        <div>
          <label className="text-[12px] uppercase tracking-wider text-slate-500 font-semibold">Arrival Notes</label>
          <textarea value={note} onChange={e => setNote(e.target.value)}
                      className="w-full mt-1 border-2 border-slate-300 rounded-lg p-2 text-[14px] min-h-[80px]"
                      placeholder="e.g. late arrival, extra pillow requested…"
                      data-testid="checkin-note"/>
        </div>
      </div>
    </OperationalDrawer>
  </>);
}
